/**
 * Onglet Configurations — Yod, T-Carré, Grand Trigone, etc.
 * Reproduit les cards du site PHP avec icônes, descriptions et badges
 * Supporte : Concentration, Combustion, Rétrograde + rétrogrades générationnels
 */

import { useState } from 'react';
import { colors } from '../../styles/tokens.js';
import { PLANET_SYMBOLS } from './planetColors.js';
import type { ChartAnalysis } from '../../types/clients.js';

/** Styles identiques au PHP v1 — couleurs + icônes Bootstrap */
const CONFIG_STYLES: Record<string, { color: string; bg: string; border: string; icon: string }> = {
  'Yod':            { color: '#b45309', bg: '#fef3c7', border: '#f59e0b', icon: 'bi-triangle' },
  'T-Carré':        { color: '#dc2626', bg: '#fee2e2', border: '#ef4444', icon: 'bi-square' },
  'Grand Trigone':  { color: '#16a34a', bg: '#dcfce7', border: '#22c55e', icon: 'bi-triangle-fill' },
  'Grand Carré':    { color: '#991b1b', bg: '#fecaca', border: '#dc2626', icon: 'bi-square-fill' },
  'Grand Rectangle': { color: '#0e7490', bg: '#ecfeff', border: '#06b6d4', icon: 'bi-bounding-box' },
  'Étoile à 6 branches': { color: '#b45309', bg: '#fffbeb', border: '#f59e0b', icon: 'bi-hexagon-fill' },
  'Cerf-Volant':    { color: '#8b5cf6', bg: '#faf5ff', border: '#a855f7', icon: 'bi-wind' },
  'Stellium':       { color: '#2563eb', bg: '#dbeafe', border: '#3b82f6', icon: 'bi-stars' },
  'Concentration':  { color: '#4f46e5', bg: '#e0e7ff', border: '#6366f1', icon: 'bi-collection' },
  'Combustion':     { color: '#ea580c', bg: '#ffedd5', border: '#f97316', icon: 'bi-fire' },
  'Rétrograde':     { color: '#9333ea', bg: '#f3e8ff', border: '#a855f7', icon: 'bi-arrow-counterclockwise' },
};

interface Props {
  configurations: ChartAnalysis['configurations'];
  retroGenerationnels?: string[];
}

export function ConfigurationsTab({ configurations, retroGenerationnels }: Props) {
  const [showHelp, setShowHelp] = useState(false);

  if (configurations.length === 0 && (!retroGenerationnels || retroGenerationnels.length === 0)) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem', color: colors.textMuted, background: colors.bgPage, borderRadius: 8 }}>
        <i className="bi bi-check-circle" style={{ fontSize: '2.5rem', color: '#22c55e', display: 'block', marginBottom: '1rem' }} />
        <p style={{ fontSize: '1.1rem', margin: '0 0 0.5rem' }}>Aucune configuration majeure détectée</p>
        <p style={{ fontSize: '0.9rem', margin: 0 }}>Le thème ne présente pas de Yod, T-Carré, Grand Trigone ou Stellium.</p>
      </div>
    );
  }

  const majeures = configurations.filter((c) => c.importance === 'majeure');
  const notables = configurations.filter((c) => c.importance !== 'majeure');

  return (
    <div>
      {/* Header */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: '1.25rem', paddingBottom: '0.75rem',
        borderBottom: `2px solid ${colors.border}`,
      }}>
        <h3 style={{ margin: 0, fontSize: '1.1rem', color: colors.text, fontWeight: 700 }}>
          <i className="bi bi-diagram-2-fill" style={{ color: colors.accent, marginRight: 8 }} />
          Configurations
        </h3>
        <button
          onClick={() => setShowHelp(!showHelp)}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            padding: '5px 12px', fontSize: '0.8rem', fontWeight: 500,
            background: showHelp ? colors.accent : '#f3f4f6',
            color: showHelp ? '#fff' : colors.textMid,
            border: 'none', borderRadius: 6, cursor: 'pointer',
            transition: 'all 0.2s', fontFamily: 'inherit',
          }}
        >
          <i className="bi bi-question-circle" />
          Aide
        </button>
      </div>

      {/* Aide */}
      {showHelp && (
        <div style={{
          background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 10,
          padding: '1.25rem', marginBottom: '1.25rem', fontSize: '0.85rem',
          lineHeight: 1.7, color: '#1e3a5f',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#0c4a6e' }}>
              Comprendre les Configurations
            </h4>
            <button
              onClick={() => setShowHelp(false)}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: '1.2rem', color: '#64748b', lineHeight: 1,
              }}
            >
              &times;
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div>
              <p style={{ fontWeight: 700, marginBottom: 4 }}>
                {'\uD83D\uDD3A'} Le Yod (Doigt de Dieu)
              </p>
              <p style={{ margin: '0 0 4px 0' }}>Configuration formee par deux planetes en sextile (60°) envoyant chacune un quinconce (150°) vers une troisieme planete (l'apex).</p>
              <p style={{ margin: 0, fontStyle: 'italic' }}><strong>Signification</strong> : Mission de vie, destin particulier. L'apex indique un point de tension qui demande des ajustements constants. Configuration karmique par excellence.</p>
            </div>

            <div>
              <p style={{ fontWeight: 700, marginBottom: 4 }}>
                {'\uD83D\uDD32'} Le T-Carre
              </p>
              <p style={{ margin: '0 0 4px 0' }}>Deux planetes en opposition (180°) formant chacune un carre (90°) avec une troisieme planete (l'apex).</p>
              <p style={{ margin: 0, fontStyle: 'italic' }}><strong>Signification</strong> : Grande tension dynamique qui pousse a l'action. L'apex concentre l'energie et represente le point de resolution. Configuration de depassement et de realisation.</p>
            </div>

            <div>
              <p style={{ fontWeight: 700, marginBottom: 4 }}>
                {'\u25B3'} Le Grand Trigone
              </p>
              <p style={{ margin: '0 0 4px 0' }}>Trois planetes formant entre elles des trigones (120°), creant un triangle equilateral dans un meme element.</p>
              <p style={{ margin: 0, fontStyle: 'italic' }}><strong>Signification</strong> : Talents naturels, fluidite dans l'element concerne. Attention a ne pas rester passif — ces dons doivent etre actives consciemment.</p>
            </div>

            <div>
              <p style={{ fontWeight: 700, marginBottom: 4 }}>
                {'\u2B1B'} Le Grand Carre
              </p>
              <p style={{ margin: '0 0 4px 0' }}>Quatre planetes formant quatre carres (90°) et deux oppositions (180°).</p>
              <p style={{ margin: 0, fontStyle: 'italic' }}><strong>Signification</strong> : Configuration rare de tres haute tension. Demande un effort constant mais confere une grande force de realisation une fois maitrisee.</p>
            </div>

            <div>
              <p style={{ fontWeight: 700, marginBottom: 4 }}>
                {'\u2B50'} Le Stellium
              </p>
              <p style={{ margin: '0 0 4px 0' }}>4 planetes ou plus dans le meme signe ou la meme maison (3 planetes = concentration notable mais pas un stellium).</p>
              <p style={{ margin: 0, fontStyle: 'italic' }}><strong>Signification</strong> : Concentration exceptionnelle d'energie. Le signe ou la maison concerne(e) devient un theme central de la vie.</p>
            </div>

            <div>
              <p style={{ fontWeight: 700, marginBottom: 4 }}>
                {'\uD83D\uDD25'} La Combustion
              </p>
              <p style={{ margin: '0 0 4px 0' }}>Planete situee a moins de 8° du Soleil (moins de 3° = combustion exacte).</p>
              <p style={{ margin: 0, fontStyle: 'italic' }}><strong>Signification</strong> : L'energie de la planete est "brulee" par le Soleil, rendant son expression plus difficile ou interiorisee.</p>
            </div>
          </div>

          <div style={{ marginTop: '1rem' }}>
            <p style={{ fontWeight: 700, marginBottom: 4 }}>
              {'\u211E'} Les Retrogrades
            </p>
            <p style={{ margin: '0 0 4px 0' }}>Planete apparaissant reculer dans le zodiaque vue depuis la Terre.</p>
            <p style={{ margin: 0, fontStyle: 'italic' }}><strong>Signification</strong> : Energie interiorisee, tournee vers l'interieur. Revision des themes associes a la planete. Seules les retrogradations personnelles et sociales (Mercure a Saturne) sont individuellement significatives.</p>
          </div>
        </div>
      )}

      {/* Résumé — identique au PHP */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1.5rem' }}>
        <div style={{
          padding: '0.5rem 1rem', borderRadius: 8,
          background: 'linear-gradient(135deg, #fef3c7, #fde68a)',
        }}>
          <span style={{ fontSize: '0.8rem', color: '#92400e' }}>Configurations majeures</span>
          <strong style={{ display: 'block', fontSize: '1.3rem', color: '#78350f' }}>{majeures.length}</strong>
        </div>
        <div style={{
          padding: '0.5rem 1rem', borderRadius: 8,
          background: '#f3f4f6',
        }}>
          <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>Configurations notables</span>
          <strong style={{ display: 'block', fontSize: '1.3rem', color: '#374151' }}>{notables.length}</strong>
        </div>
      </div>

      {/* Cards — layout identique au PHP (grid) */}
      <div style={{ display: 'grid', gap: '1rem' }}>
        {configurations.map((config, i) => {
          const cStyle = CONFIG_STYLES[config.type] ?? { color: '#6b7280', bg: '#f3f4f6', border: '#9ca3af', icon: 'bi-circle' };
          const isMajor = config.importance === 'majeure';
          const lieu = config.details?.lieu as string | undefined;

          return (
            <div key={i} style={{
              padding: '1.25rem',
              borderRadius: '0 8px 8px 0',
              background: cStyle.bg,
              borderLeft: `4px solid ${cStyle.border}`,
            }}>
              {/* Header: titre + badge — identique au PHP */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <i className={`bi ${cStyle.icon}`} style={{ fontSize: '1.5rem', color: cStyle.color }} />
                  <div>
                    <span style={{ fontWeight: 700, fontSize: '1.1rem', color: cStyle.color }}>
                      {config.type}
                    </span>
                    {lieu && (
                      <div style={{ fontSize: '0.85rem', color: colors.text }}>{lieu}</div>
                    )}
                  </div>
                </div>
                <span style={{
                  fontSize: '0.75rem', padding: '4px 10px', borderRadius: 12,
                  background: isMajor ? '#fef3c7' : '#f3f4f6',
                  color: isMajor ? '#92400e' : '#6b7280',
                  fontWeight: 600,
                }}>
                  {isMajor ? '★ MAJEURE' : 'Notable'}
                </span>
              </div>

              {/* Tags planètes — style pill comme le PHP */}
              {config.planetes && config.planetes.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.75rem' }}>
                  {config.planetes.map((p) => {
                    const pSymbol = PLANET_SYMBOLS[p] ?? '•';
                    const isApex = config.details?.apex === p;
                    const isFocus = config.details?.focus === p;
                    return (
                      <span key={p} style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        padding: '4px 10px', background: '#fff', borderRadius: 16,
                        fontSize: '0.85rem',
                        border: (isApex || isFocus) ? `2px solid ${cStyle.border}` : '1px solid #e5e7eb',
                      }}>
                        <span style={{ fontFamily: "'Noto Sans Symbols 2', sans-serif" }}>{pSymbol}</span>
                        {p}
                        {isApex && (
                          <span style={{ fontSize: '0.7rem', color: cStyle.color, fontWeight: 700 }}>APEX</span>
                        )}
                        {isFocus && (
                          <span style={{ fontSize: '0.7rem', color: cStyle.color, fontWeight: 700 }}>FOCUS</span>
                        )}
                      </span>
                    );
                  })}
                  {config.details?.orbe != null && (
                    <span style={{ fontSize: '0.85rem', color: colors.textMuted, alignSelf: 'center' }}>
                      Orbe: {String(config.details.orbe)}°
                    </span>
                  )}
                </div>
              )}

              {/* Description */}
              {config.description && (
                <p style={{ margin: 0, fontSize: '0.9rem', color: colors.text, lineHeight: 1.5 }}>
                  {config.description}
                </p>
              )}
            </div>
          );
        })}
      </div>

      {/* Rétrogrades générationnels — identique au PHP */}
      {retroGenerationnels && retroGenerationnels.length > 0 && (
        <div style={{
          marginTop: '1.5rem', padding: '1rem',
          background: '#faf5ff', borderRadius: 8,
          border: '1px solid #e9d5ff',
        }}>
          <h4 style={{ margin: '0 0 0.75rem', fontSize: '0.95rem', color: '#7c3aed' }}>
            <i className="bi bi-info-circle" style={{ marginRight: 6 }} />
            Rétrogrades générationnels (non significatifs individuellement)
          </h4>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
            {retroGenerationnels.map((nom) => {
              const pSymbol = PLANET_SYMBOLS[nom] ?? '•';
              return (
                <span key={nom} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  padding: '4px 10px', background: '#fff', borderRadius: 16,
                  fontSize: '0.85rem', color: '#9333ea',
                }}>
                  <span style={{ fontFamily: "'Noto Sans Symbols 2', sans-serif" }}>{pSymbol}</span>
                  {nom} ℞
                </span>
              );
            })}
          </div>
          <p style={{ margin: '0.75rem 0 0', fontSize: '0.8rem', color: '#7c3aed' }}>
            Les planètes transpersonnelles (Uranus, Neptune, Pluton) sont rétrogrades plusieurs mois par an.
            Leur rétrogradation est partagée par une génération entière et n'a pas de signification individuelle marquée.
          </p>
        </div>
      )}
    </div>
  );
}
